import { XAxisOptions, AxisTypeValue } from 'highcharts';
import { colors } from '@dexma/ui-components';
import { ChartAxis, ChartSerie, ChartSeriesType, DatetimeChartAxisMeta, ChartAxisType, Resolution } from 'domain/entity/widgets/ChartData';
import { WidgetType } from 'domain/entity/widget';
import Branding from 'domain/entity/branding';
import { ChartAxisVisible } from 'adapter/in/common/components/widgets/Chart';
import { parseSerie } from 'adapter/in/common/components/widgets/Chart/parseSerie';
import { ChartSeriesOptionsType } from 'adapter/in/common/components/widgets/Chart/types';

const transformChartSeriesType = (type: ChartSeriesType) => {
    switch (type) {
        case ChartSeriesType.CHART_LIMIT:
        case ChartSeriesType.TOOLTIP:
        case ChartSeriesType.GROUPED_LINE:
            return ChartSeriesType.LINE;
        case ChartSeriesType.GROUPED_BAR:
            return ChartSeriesType.COLUMN;
        default:
            return type;
    }
};

export const parseSeries = (
    series: ChartSerie[],
    widgetType: WidgetType,
    branding: Branding,
    isLoading: boolean,
    axesY: ChartAxis[]
): ChartSeriesOptionsType[] => {
    const firstSerieOfEachStackMap = new Map<string, number>();
    const firstSerieOfEachStackIndex = new Set<number>();
    series.forEach((serie, index) => {
        if (serie.stack && serie.values && serie.values.length > 0 && !firstSerieOfEachStackMap.has(serie.stack)) {
            firstSerieOfEachStackMap.set(serie.stack, index);
            firstSerieOfEachStackIndex.add(index);
        } else if (!serie.stack && serie.type === ChartSeriesType.COLUMN) {
            firstSerieOfEachStackIndex.add(index);
        }
    });
    return series.map(parseSerie(widgetType, branding, isLoading, axesY, firstSerieOfEachStackIndex)).flat();
};

export const parseXAxis = (chartAxisX: ChartAxis, series: ChartSerie[], isLoading: boolean, isOnEdit: boolean, i: number): XAxisOptions => {
    const xAxis = {
        crosshair: !(isLoading || isOnEdit),
        type: isLoading ? 'datetime' : (chartAxisX.type.toLowerCase() as AxisTypeValue),
    };

    switch (chartAxisX.type) {
        case ChartAxisType.CATEGORIZED: {
            return { ...xAxis, categories: chartAxisX.meta.categories };
        }
        case ChartAxisType.NUMERIC:
            return {
                title: {
                    text: isLoading ? 'Sample text' : chartAxisX.title,
                },
                labels: {
                    format: isLoading ? ' ' : `{value} ${chartAxisX.units}`,
                },
                opposite: i % 2 === 1,
            };
        case ChartAxisType.DATETIME:
        default: {
            const datetimeMeta = chartAxisX.meta as DatetimeChartAxisMeta;
            return {
                ...xAxis,
                plotBands: isLoading
                    ? undefined
                    : checkIfNeedsColoring(series, datetimeMeta.from, datetimeMeta.to, datetimeMeta.resolution),
            };
        }
    }
};

export const parseYAxis = (chartAxisY: ChartAxisVisible, isLoading: boolean, i: number) => ({
    title: {
        text: isLoading ? 'Sample text' : chartAxisY.title,
    },
    labels: {
        format: isLoading ? ' ' : `{value} ${chartAxisY.units}`,
    },
    opposite: i % 2 === 1,
    yAxis: i,
    index: i,
    visible: chartAxisY.visible,
    plotLines: i === 0 ? [{ color: '#636363', value: 0, width: 1.1 }] : undefined,
});

const checkIfNeedsColoring = (
    series: ChartSerie[],
    from: Date,
    to: Date,
    resolution: Resolution
): { color: string; from: number; to: number }[] | undefined => {
    if ([Resolution.B, Resolution.W, Resolution.M, Resolution.Y].includes(resolution)) {
        return undefined;
    }

    return colorWeekends(series, from, to);
};

export const colorWeekends = (series: ChartSerie[], from: Date, to: Date): { color: string; from: number; to: number }[] => {
    let iterator = new Date(from);
    iterator.setDate(from.getUTCDate() + ((7 - from.getUTCDay()) % 7));
    const plots = [];
    const weekendColor = colors.gray100;
    const millisInADay = 86400000;
    const endOfDay = 86399999;

    const hasDailyResolutionAndColumnSeries = (chartSeries: ChartSerie[]): boolean =>
        chartSeries.some((serie) => transformChartSeriesType(serie.type) === ChartSeriesType.COLUMN && serie.resolution === Resolution.D);

    while (iterator.getTime() <= to.getTime()) {
        let startWeekend = iterator.getTime() - millisInADay;
        let endWeekend = iterator.getTime() + endOfDay;
        if (hasDailyResolutionAndColumnSeries(series)) {
            startWeekend -= millisInADay / 2; // Should start the plotband at 12:00 PM of friday
            endWeekend -= millisInADay / 2; // Should finish the plotband at 12:00 PM of sunday
        }

        plots.push({
            color: weekendColor,
            from: startWeekend,
            to: endWeekend,
        });

        iterator = new Date(iterator.getTime() + millisInADay * 7);
    }

    return plots;
};
